import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useMonitoring } from '../context/MonitoringContext';

export default function Sidebar({ collapsed, onToggleCollapse }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { isDark, toggleTheme } = useTheme();
    const { logout, user } = useAuth();
    const { servers, connectionStatus } = useMonitoring();

    const onlineCount = servers.filter((s) => s.status?.toLowerCase() === 'online').length;
    const serverCount = servers.length;

    const isActive = (path) => location.pathname === path;

    // Role-based visibility — admins get everything
    const admin = user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'administrator';
    const canManageUpdates = admin || user?.updates === true;
    const canViewLogs      = admin || !!user?.logs;
    const canViewSettings  = admin;

    return (
        <aside className={`portal-sidebar ${collapsed ? 'collapsed' : ''}`}>
            {/* Branding and Collapse Button */}
            <div
                className="sidebar-brand-wrapper"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: collapsed ? 'center' : 'space-between',
                    gap: '10px',
                    width: '100%',
                }}
            >
                {!collapsed && (
                    <div className="sidebar-brand">
                        <img
                            className="sidebar-logo"
                            src={`${process.env.PUBLIC_URL}/favicon.png`}
                            alt="HeadOffice Logo"
                        />
                        <h2 className="sidebar-title">Headoffice Monitor</h2>
                    </div>
                )}
                {collapsed && (
                    <img
                        className="sidebar-logo"
                        src={`${process.env.PUBLIC_URL}/favicon.png`}
                        alt="HeadOffice Logo"
                        style={{ margin: 0 }}
                    />
                )}
                <button
                    className="sidebar-collapse-btn"
                    onClick={onToggleCollapse}
                    title={collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'rgba(255, 255, 255, 0.6)',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 'var(--radius-sm)',
                        transition: 'color var(--transition-fast)',
                    }}
                >
                    {collapsed ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 18 15 12 9 6" />
                        </svg>
                    ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    )}
                </button>
            </div>

            {/* Telemetry Stats Card */}
            {!collapsed ? (
                <div className="sidebar-status-card">
                    <div className="sidebar-status-item">
                        <span>Monitor</span>
                        <span className="sidebar-status-val">
                            <span
                                className="sidebar-dot"
                                style={{
                                    backgroundColor:
                                        connectionStatus === 'connected' ? 'var(--success)'
                                        : connectionStatus === 'connecting' ? 'var(--warning, #f59e0b)'
                                        : 'var(--error)',
                                    color:
                                        connectionStatus === 'connected' ? 'var(--success)'
                                        : connectionStatus === 'connecting' ? 'var(--warning, #f59e0b)'
                                        : 'var(--error)',
                                }}
                            />
                            {connectionStatus === 'connected' ? 'Connected'
                             : connectionStatus === 'connecting' ? 'Connecting...'
                             : 'Offline'}
                        </span>
                    </div>
                    <div className="sidebar-status-item">
                        <span>Active Servers</span>
                        <span className="sidebar-status-val">{serverCount} servers</span>
                    </div>
                    <div className="sidebar-status-item">
                        <span>Online Status</span>
                        <span className="sidebar-status-val" style={{ color: 'var(--success)' }}>
                            {onlineCount} active
                        </span>
                    </div>
                </div>
            ) : (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '8px 0',
                    }}
                    title={
                        connectionStatus === 'connected'
                            ? `Connected (${onlineCount}/${serverCount} online)`
                            : connectionStatus === 'connecting'
                            ? 'Connecting to Telemetry...'
                            : 'Telemetry Offline'
                    }
                >
                    <span
                        className="sidebar-dot"
                        style={{
                            backgroundColor:
                                connectionStatus === 'connected' ? 'var(--success)'
                                : connectionStatus === 'connecting' ? 'var(--warning, #f59e0b)'
                                : 'var(--error)',
                            color:
                                connectionStatus === 'connected' ? 'var(--success)'
                                : connectionStatus === 'connecting' ? 'var(--warning, #f59e0b)'
                                : 'var(--error)',
                            margin: 0,
                            width: '12px',
                            height: '12px',
                            boxShadow:
                                connectionStatus === 'connected' ? '0 0 8px var(--success)'
                                : connectionStatus === 'connecting' ? '0 0 8px var(--warning, #f59e0b)'
                                : '0 0 8px var(--error)',
                        }}
                    />
                </div>
            )}

            {/* Navigation Buttons */}
            <nav className="sidebar-nav">
                <button
                    className={`sidebar-nav-btn ${isActive('/dashboard') ? 'active' : ''}`}
                    onClick={() => navigate('/dashboard')}
                    title={collapsed ? 'Dashboard' : undefined}
                    style={collapsed ? { justifyContent: 'center', padding: '12px' } : {}}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
                        <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
                        <line x1="6" y1="6" x2="6.01" y2="6" />
                        <line x1="6" y1="18" x2="6.01" y2="18" />
                    </svg>
                    {!collapsed && <span>Dashboard</span>}
                </button>

                <button
                    className={`sidebar-nav-btn ${isActive('/servers') ? 'active' : ''}`}
                    onClick={() => navigate('/servers')}
                    title={collapsed ? 'Servers' : undefined}
                    style={collapsed ? { justifyContent: 'center', padding: '12px' } : {}}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="6" rx="1" />
                        <rect x="3" y="14" width="18" height="6" rx="1" />
                        <line x1="7" y1="7" x2="7.01" y2="7" />
                        <line x1="7" y1="17" x2="7.01" y2="17" />
                    </svg>
                    {!collapsed && <span>Servers</span>}
                </button>

                {canManageUpdates && (
                    <button
                        className={`sidebar-nav-btn ${isActive('/packages') ? 'active' : ''}`}
                        onClick={() => navigate('/packages')}
                        title={collapsed ? 'Packages' : undefined}
                        style={collapsed ? { justifyContent: 'center', padding: '12px' } : {}}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
                            <polygon points="12 22.08 12 12 3 6.92 3 17.08 12 22.08" />
                            <polygon points="12 22.08 21 17.08 21 6.92 12 12 12 22.08" />
                            <polygon points="12 12 21 6.92 12 1.84 3 6.92 12 12" />
                        </svg>
                        {!collapsed && <span>Packages</span>}
                    </button>
                )}

                {canViewLogs && (
                    <button
                        className={`sidebar-nav-btn ${isActive('/logs') ? 'active' : ''}`}
                        onClick={() => navigate('/logs')}
                        title={collapsed ? 'Logs' : undefined}
                        style={collapsed ? { justifyContent: 'center', padding: '12px' } : {}}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                            <polyline points="10 9 9 9 8 9" />
                        </svg>
                        {!collapsed && <span>Logs</span>}
                    </button>
                )}

                {canViewSettings && (
                    <>
                        <button
                            className={`sidebar-nav-btn ${isActive('/settings') ? 'active' : ''}`}
                            onClick={() => navigate('/settings')}
                            title={collapsed ? 'Settings' : undefined}
                            style={collapsed ? { justifyContent: 'center', padding: '12px' } : {}}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="3" />
                                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                            </svg>
                            {!collapsed && <span>Settings</span>}
                        </button>
                        <button
                            className={`sidebar-nav-btn ${isActive('/users') ? 'active' : ''}`}
                            onClick={() => navigate('/users')}
                            title={collapsed ? 'User Management' : undefined}
                            style={collapsed ? { justifyContent: 'center', padding: '12px' } : {}}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                            {!collapsed && <span>User Management</span>}
                        </button>
                        <button
                            className={`sidebar-nav-btn ${isActive('/groups') ? 'active' : ''}`}
                            onClick={() => navigate('/groups')}
                            title={collapsed ? 'Group Configuration' : undefined}
                            style={collapsed ? { justifyContent: 'center', padding: '12px' } : {}}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="7" height="7" />
                                <rect x="14" y="3" width="7" height="7" />
                                <rect x="14" y="14" width="7" height="7" />
                                <rect x="3" y="14" width="7" height="7" />
                            </svg>
                            {!collapsed && <span>Group Configs</span>}
                        </button>
                    </>
                )}
            </nav>

            {/* Logout Button */}
            <div style={collapsed ? { display: 'flex', justifyContent: 'center', padding: '12px 0 0 0', borderTop: '1px solid var(--sidebar-border)' } : { padding: '16px 0 0 0', borderTop: '1px solid var(--sidebar-border)' }}>
                <button
                    className="portal-theme-toggle"
                    onClick={logout}
                    title="Log Out"
                    style={{
                        borderColor: 'rgba(239, 68, 68, 0.15)',
                        background: 'rgba(239, 68, 68, 0.04)',
                        color: 'var(--brand-red)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        gap: '10px',
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid rgba(239, 68, 68, 0.2)',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: 'var(--fs-xs)',
                        transition: 'all var(--transition-fast)',
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    {!collapsed && <span>Log Out</span>}
                </button>
            </div>

            {/* Theme Toggle Button */}
            <div className="sidebar-theme-row" style={collapsed ? { paddingTop: '8px' } : { paddingTop: '12px' }}>
                <button
                    className="portal-theme-toggle"
                    onClick={toggleTheme}
                    title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                    style={collapsed ? { padding: '8px', justifyContent: 'center' } : {}}
                >
                    {isDark ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="5" />
                            <line x1="12" y1="1" x2="12" y2="3" />
                            <line x1="12" y1="21" x2="12" y2="23" />
                            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                            <line x1="1" y1="12" x2="3" y2="12" />
                            <line x1="21" y1="12" x2="23" y2="12" />
                            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                        </svg>
                    ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                        </svg>
                    )}
                    {!collapsed && <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>}
                </button>
            </div>
        </aside>
    );
}